import React, { useCallback, useEffect } from "react";
import DadosEstadoCivilVendedorDefaultForm from "./DadosEstadoCivilVendedorDefaultForm";
import { useDispatch, useSelector } from "react-redux";
import {
  getComplementoOperacao,
  isGravandoComplementoOperacao,
} from "../../../../../selectors/operacao.selectors";
import useCompleteForm from "../../../../../hooks/useCompleteForm";
import validators from "../../../../../utils/validators";
import { actions } from "../../../../../actions/operacao.actions";
import {
  removeNonDigitsFromString,
  transformToDate,
} from "../../../../../utils/basic";
import formatters from "../../../../../utils/formatters";

const DadosEstadoCivilVendedorDefaultFormConnected = ({
  handleGoTo0,
  events,
  setParticipante,
  participante: vendedor,
  ...otherProps
}) => {
  const dispatch = useDispatch();
  const {
    operacao,
    dominios: {
      estadoCivil: estadosCivis,
      regimeComunhao: regimesComunhao,
      documentoPessoal: documentosPessoais,
      genero: generos,
      profissao: profissoes,
    },
  } = useSelector(getComplementoOperacao);
  const submitting = useSelector(isGravandoComplementoOperacao);

  const estadoCivilInicial =
    estadosCivis.find((e) => e.id === vendedor?.estadoCivil?.id) ?? null;
  const estadoCivilConjugeInicial =
    estadosCivis.find((e) => e.id === vendedor?.conjuge?.estadoCivil?.id) ??
    null;

  const requiredIfContainsSpouse = (value, values) => {
    const exibeConjuge = values.estadoCivil?.conjuge || values.uniaoEstavel;

    if (!exibeConjuge) {
      return true;
    }

    if (value !== undefined && value !== null && value !== "" && value !== {})
      return true;
    return "Campo obrigatório";
  };
  const requiredIfContainsSpouseNotMarried = (value, values) => {
    const exibeConjuge = values.estadoCivil?.conjuge || values.uniaoEstavel;
    const exibeExtra = exibeConjuge && values.isUniaoEstavel;

    if (!exibeExtra) {
      return true;
    }

    if (value !== undefined && value !== null && value !== "" && value !== {})
      return true;
    return "Campo obrigatório";
  };

  const callback = useCallback(
    (data) => {
      const c = data.operacao.participantes.find((p) => p.id === vendedor.id);
      if (!!c) {
        setParticipante({
          ...c,
          fluxo: data.validacao.completa.participantes.find(
            (f) => f.id === c?.id
          )?.etapas,
        });
      }
      handleGoTo0();
    },
    [handleGoTo0, setParticipante, vendedor]
  );

  const [formProps, handleSubmit] = useCompleteForm({
    rules: () => ({
      estadoCivil: validators.object({ required: true }),
      dataCasamento: validators.date({
        custom: {
          requiredIfContainsSpouse,
        },
      }),
      regimeComunhao: validators.object({
        custom: {
          requiredIfContainsSpouse,
        },
      }),
      conjuge: {
        nome: validators.string({
          custom: {
            requiredIfContainsSpouse,
            nomeCompleto: (value) =>
              !value ||
              (value.indexOf(" ") > 0 &&
                value.substring(value.indexOf(" ") + 1).length > 0) ||
              "Nome informado deve ser completo",
          },
        }),
        cpfCnpj: validators.cpf({
          custom: {
            requiredIfContainsSpouse,
          },
        }),
        dataNascimento: validators.date({
          custom: {
            requiredIfContainsSpouse,
          },
        }),
        genero: validators.object({
          custom: {
            requiredIfContainsSpouse,
          },
        }),
        documentoPessoal: validators.object({
          custom: {
            requiredIfContainsSpouse,
          },
        }),
        numeroDocumentoPessoal: validators.string({
          custom: {
            requiredIfContainsSpouse,
          },
          maxLength: 100,
        }),
        orgaoDocumentoPessoal: validators.string({
          custom: {
            requiredIfContainsSpouse,
          },
          maxLength: 100,
        }),
        ufOrgaoDocumentoPessoal: validators.string({
          custom: {
            requiredIfContainsSpouse,
          },
        }),
        dataExpedicaoDocumentoPessoal: validators.date({
          custom: {
            requiredIfContainsSpouse,
          },
          min: "01/01/1900",
          max: formatters.dates.short(new Date()),
        }),
        estadoCivil: validators.object({
          custom: { requiredIfContainsSpouseNotMarried },
        }),
        telefone: validators.phone({
          custom: {
            requiredIfContainsSpouse
          }
        }),
        cargo: validators.string({
          custom: {
            requiredIfContainsSpouse,
          },
        }),
        nomeProcurador: validators.string({ required: true }),
        cpfProcurador: validators.cpf({ required: true }),
      },
    }),
    initialValues: useCallback(
      () => ({
        uniaoEstavel: false,
        regimeComunhao: null,
        dataCasamento: "",
        ...vendedor,
        estadoCivil: estadoCivilInicial,
        conjuge: {
          nome: "",
          cpfCnpj: "",
          dataNascimento: "",
          email: "",
          documentoPessoal: null,
          numeroDocumentoPessoal: "",
          orgaoDocumentoPessoal: "",
          ufOrgaoDocumentoPessoal: "",
          dataExpedicaoDocumentoPessoal: "",
          genero: null,
          telefone: "",
          cargo: "",
          ...vendedor?.conjuge,
          estadoCivil: estadoCivilConjugeInicial,
        },
      }),
      [vendedor, estadoCivilInicial, estadoCivilConjugeInicial]
    ),
    handleSubmit: useCallback(
      (values) => {
        const exibeUniaoEstavel = values.estadoCivil?.uniaoEstavel;
        const exibeConjuge = values.estadoCivil?.conjuge || values.uniaoEstavel;

        dispatch(
          actions.gravarComplemento.request({
            callback,
            instituicao: operacao.codigoInstituicao,
            passo: "dados-estado-civil-vendedor",
            participante: {
              id: vendedor?.id,
              idEstadoCivil: values.estadoCivil?.id,
              uniaoEstavel: exibeUniaoEstavel && values.uniaoEstavel,
              dataCasamento: !!exibeConjuge
                ? transformToDate(values.dataCasamento)
                : null,
              idRegimeComunhao: !!exibeConjuge
                ? values.regimeComunhao?.id
                : null,
              conjuge: !!exibeConjuge
                ? {
                  nome: values.conjuge.nome,
                  cpfCnpj: removeNonDigitsFromString(values.conjuge.cpfCnpj),
                  dataNascimento: transformToDate(
                    values.conjuge.dataNascimento
                  ),
                  idGenero: values.conjuge.genero?.id,
                  idDocumentoPessoal: values.conjuge.documentoPessoal?.id,
                  numeroDocumentoPessoal:
                    values.conjuge.numeroDocumentoPessoal,
                  orgaoDocumentoPessoal: values.conjuge.orgaoDocumentoPessoal,
                  ufOrgaoDocumentoPessoal:
                    values.conjuge.documentoPessoal?.descricao === "RNE" ? null : values.conjuge.ufOrgaoDocumentoPessoal,
                  dataExpedicaoDocumentoPessoal: transformToDate(
                    values.conjuge.dataExpedicaoDocumentoPessoal
                  ),
                  conjugeVendedor: values.conjuge.conjugeVendedor,
                  email: values.conjuge.email,
                  telefone: values.conjuge.telefone,
                  cargo: values.conjuge.cargo,
                  idEstadoCivil:
                    !!exibeUniaoEstavel && values.uniaoEstavel
                      ? values.conjuge?.estadoCivil?.id
                      : null,
                  possuiProcuracao: values.conjuge.possuiProcuracao,
                  nomeProcurador: values.conjuge.possuiProcuracao
                    ? values.conjuge.nomeProcurador
                    : null,
                  cpfProcurador: values.conjuge.possuiProcuracao
                    ? removeNonDigitsFromString(values.conjuge.cpfProcurador)
                    : null,
                }
                : null,
            },
            operacao: {
              id: operacao.id,
            },
          })
        );
      },
      [dispatch, vendedor, callback, operacao]
    ),
  });
  const { setValue, watch } = formProps;

  const estadoCivil = watch("estadoCivil");
  const isUniaoEstavel = watch("uniaoEstavel");
  const isEstrangeiro = watch("conjuge.documentoPessoal")?.id === 4;

  const exibeUniaoEstavel = !!estadoCivil?.uniaoEstavel;
  const exibeConjuge = !!estadoCivil?.conjuge || isUniaoEstavel;

  useEffect(() => {
    if (!!estadoCivil?.conjuge) {
      setValue("uniaoEstavel", false);
    }
  }, [estadoCivil, setValue, isUniaoEstavel]);

  return (
    <DadosEstadoCivilVendedorDefaultForm
      {...otherProps}
      estadosCivis={estadosCivis}
      regimesComunhao={regimesComunhao}
      generos={generos}
      events={events}
      fluxo={vendedor.fluxo}
      formProps={formProps}
      handleSubmit={handleSubmit}
      exibeConjuge={exibeConjuge}
      exibeUniaoEstavel={exibeUniaoEstavel}
      documentosPessoais={documentosPessoais}
      isUniaoEstavel={isUniaoEstavel}
      submitting={submitting}
      profissoes={profissoes}
      isEstrangeiro={isEstrangeiro}
    />
  );
};

export default DadosEstadoCivilVendedorDefaultFormConnected;
